import dapiConfig from "../configs/dapi";
import { accessToken } from "./utils/tokenService";

const BASE_URL = dapiConfig.api.domain;

export interface DapiDocSplit {
  // "verbal" is a 口头单 — a real shipment with no 澳洲订单号 behind it, so it has no
  // order in China Order Tracker to link to and is stored without a Run.
  type: "order" | "stock" | "verbal";
  doc_no: string | null;
  qty: number | null;
}

// One 合计 row, as the packing list states it. There is one per product category —
// 胶盒合计 (cartons), 胶片合计 (pallets), sometimes split PP/PET — and they are NOT
// addable: a 板 is not a 箱. These are the factory's product subtotals and exclude
// one-off lines, so they need not add up to the container_total_* figures.
export interface DapiSubtotal {
  label: string;
  gross_kg: number | null;
  net_kg: number | null;
  count: number | null;
  unit: string | null;
}

export interface DapiShipmentItem {
  // The factory's 外箱标, as data URIs. Present only when convertXlsx asked for them,
  // and never stored — they are what the operator reads to decide which run a line
  // belongs to, not a record of the shipment.
  label_images?: { data: string; anchor_row: number }[];
  seq: number | null;
  item_type: string | null;
  // A one-off line ("misc") states only a name, a count, that count's unit and a
  // gross weight — no item code, no order reference, no carton structure.
  unit: string | null;
  pack_count: number | null;
  cn_item_name: string | null;
  material: string | null;
  spec: string | null;
  feature: string | null;
  cn_item_code: string | null;
  doc_nos: string[];
  from_stock: boolean;
  doc_splits: DapiDocSplit[];
  pcs_per_box: number | null;
  boxes: number | null;
  total_qty: number | null;
  gross_per_box: number | null;
  total_gross_kg: number | null;
  net_per_box: number | null;
  total_net_kg: number | null;
  pallet_weight: number | null;
  paper_core_weight: number | null;
  actual_gross_kg: number | null;
  outer_carton_mark: string | null;
  remarks: string | null;
  image_1?: string;
  image_2?: string;
  image_3?: string;
}

export interface DapiConvertResponse {
  container_no: string | null;
  container_type: string | null;
  vessel: string | null;
  arrival_date: string | null;
  freight: number | null;
  responsible_person: string | null;
  departure_date: string | null;
  shipping_line: string | null;
  free_days: number | null;
  report_date: string | null;
  seal: string | null;
  destination: string | null;
  forwarder: string | null;
  vgm_kg: number | null;
  remarks: string | null;
  source_file: string;
  items: DapiShipmentItem[];
  container_total_gross_kg: number;
  container_total_net_kg: number;
  container_total_boxes: number;
  subtotals: DapiSubtotal[];
}

// A 胶片装柜明细 details a film line its packing list already states, so /convert
// identifies it rather than parsing it — the file is filed against that container
// instead of becoming a shipment of its own. Nothing to review, nothing to submit.
export interface DapiFilmLogResponse {
  kind: "film_log";
  container_no: string | null;
  file_name: string;
}

export type DapiConvertResult = DapiConvertResponse | DapiFilmLogResponse;

export const isFilmLog = (result: DapiConvertResult): result is DapiFilmLogResponse =>
  (result as DapiFilmLogResponse).kind === "film_log";

export async function convertXlsx(
  fileUrl: string,
  // The 外箱标 pictures, for the matching screen to show. Off by default in DAPI
  // because they are the bulk of the response — a median of 138KB against 1KB —
  // and only that screen has any use for them.
  includeLabelImages = false,
): Promise<DapiConvertResult> {
  const url = BASE_URL.endsWith("/") ? `${BASE_URL}convert` : `${BASE_URL}/convert`;

  const { accessToken: token } = await accessToken();

  const response = await fetch(url, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({ file_url: fileUrl, include_label_images: includeLabelImages }),
  });

  if (!response.ok) {
    const detail = await response.text().catch(() => "");
    throw new Error(detail || `DAPI error: ${response.status}`);
  }

  return response.json();
}
